import { saveOAuthReturn } from './oauthReturn';
import { logger } from '../utils/logger';
import platformService from '../services/platformService';
import { API_URL } from '../services/api';
import {
  ImportDateRange,
  sanitizeImportDateRange,
  getDefaultImportDateRange,
} from './importDateRange';

export interface ShopifyImportOptions {
  source: string;
  user: any;
  setUploadError: (msg: string | null) => void;
  setImporting: (val: boolean) => void;
  navigate: any;
  companyId?: number;
  shop?: string; // add this
  dateRange?: ImportDateRange;
}

interface ShopifyOAuthResponse {
  auth_url?: string;
  detail?: string;
  [key: string]: any;
}

/**
 * Centralized Shopify import/connect handler for all flows
 * Ensures proper company context and error handling
 */
export const handleShopifyImport = async ({
  source,
  user,
  setUploadError,
  setImporting,
  navigate,
  companyId,
  shop, // new
  dateRange
}: ShopifyImportOptions & { shop?: string; dateRange?: ImportDateRange }): Promise<void> => {
  setImporting(true);
  setUploadError(null);

  const token = localStorage.getItem('token');
  if (!token) {
    setUploadError('No authentication token found. Please log in again.');
    setImporting(false);
    return;
  }
  const targetCompanyId = companyId ?? user.primary_company_id;
  if (!targetCompanyId) {
    setUploadError('No company associated with this account. Please set up your company first.');
    setImporting(false);
    return;
  }
  try {
    logger.log('[Shopify Import] Starting OAuth authorization process');
    const apiUrl = `${API_URL}/api/v1/platforms/shopify/oauth/authorize`;
    const { startDate, endDate } = sanitizeImportDateRange(dateRange);
    const fetchPayload: any = {
      company_id: String(targetCompanyId),
      redirect_uri: 'https://gotrutax.com/shopify/callback',
      source: source,
      start_date: startDate,
      end_date: endDate
    };
    if (shop) fetchPayload.shop = shop.endsWith('.myshopify.com') ? shop : `${shop}.myshopify.com`;
    
    logger.log('[Shopify Import] OAuth request payload:', { ...fetchPayload, company_id: fetchPayload.company_id });
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-API-Key': process.env.REACT_APP_API_KEY || 'test-key-123'
      },
      body: JSON.stringify(fetchPayload)
    });
    logger.log('[Shopify Import] OAuth response status:', response.status);
    const responseData: ShopifyOAuthResponse = await response.json().catch(() => ({}));
    logger.log('[Shopify Import] OAuth response data:', responseData);
    
    if (!response.ok) {
      logger.error('[Shopify Import] OAuth authorization failed:', { status: response.status, data: responseData });
      setUploadError(responseData.detail || 'Failed to get Shopify OAuth URL');
      setImporting(false);
      return;
    }
    if (responseData.auth_url) {
      logger.log('[Shopify Import] Redirecting to Shopify OAuth URL:', responseData.auth_url);
      logger.log('[Shopify Import] OAuth state:', responseData.state);
      logger.log('[Shopify Import] Integration ID:', responseData.integration_id);
      
      saveOAuthReturn('shopify', source, targetCompanyId);
      setTimeout(() => {
        window.location.href = responseData.auth_url!;
      }, 100);
      return;
    } else {
      logger.error('[Shopify Import] No auth URL in response');
      setUploadError('No auth URL provided in the response. Please check that your Shopify store name is correct.');
      setImporting(false);
      return;
    }
  } catch (error: any) {
    setUploadError(error.message || 'Shopify OAuth failed');
    setImporting(false);
    return;
  }
};

/**
 * Import Shopify sales transactions for a company after OAuth
 * Shows progress using setImporting and setUploadError
 */
export const importShopifyTransactions = async ({
  companyId,
  integrationId,
  setImporting,
  setUploadError,
  onProgress,
  onComplete,
  dateRange
}: {
  companyId?: number,
  integrationId?: number,
  setImporting: (val: boolean) => void,
  setUploadError: (msg: string | null) => void,
  onProgress?: (msg: string) => void,
  onComplete?: (success: boolean, error?: string, integration?: any) => void,
  dateRange?: ImportDateRange
}) => {
  setImporting(true);
  setUploadError(null);
  onProgress && onProgress('Starting Shopify import...');

  try {
    let resolvedCompanyId = companyId;
    let shopifyIntegration: any = null;

    if (integrationId) {
      try {
        shopifyIntegration = await platformService.getIntegration(integrationId);
        if (shopifyIntegration?.company_id && !resolvedCompanyId) {
          resolvedCompanyId = Number(shopifyIntegration.company_id);
        }
      } catch (integrationError: any) {
        logger.error('[Shopify Import] Failed to load integration by id:', integrationError);
        // Continue to fallback lookup below
      }
    }

    if (!shopifyIntegration) {
      if (!resolvedCompanyId) {
        // As a last resort, attempt to fetch all integrations the user can access
        const integrations = await platformService.listIntegrations();
        shopifyIntegration = integrations.find(
          (i: any) => i.platform?.name?.toLowerCase() === 'shopify' && i.is_active && i.access_token
        );
      } else {
        const integrations = await platformService.listIntegrations(resolvedCompanyId);
        shopifyIntegration = integrations.find(
          (i: any) => i.platform?.name?.toLowerCase() === 'shopify' && i.is_active && i.access_token
        );
      }
    }

    if (!shopifyIntegration) {
      const message = integrationId
        ? 'Unable to find the Shopify integration that was just connected. Please refresh and try again.'
        : 'No active Shopify integration found for this company.';
      setUploadError(message);
      onComplete && onComplete(false, message);
      return;
    }

    if (!resolvedCompanyId && shopifyIntegration.company_id) {
      resolvedCompanyId = Number(shopifyIntegration.company_id);
    }

    onProgress && onProgress('Importing sales transactions from Shopify...');

    const { startDate: startISO, endDate: endISO } = dateRange
      ? sanitizeImportDateRange(dateRange)
      : getDefaultImportDateRange();

    try {
      await platformService.syncTransactions(shopifyIntegration.id, {
        startDate: startISO,
        endDate: endISO,
        background: true
      });
    } catch (err: any) {
      const message = err?.message || 'Shopify import failed.';
      setUploadError(message);
      onComplete && onComplete(false, message, shopifyIntegration);
      return;
    }

    onProgress && onProgress('Shopify import complete.');
    onComplete && onComplete(true, undefined, shopifyIntegration);
  } catch (err: any) {
    const message = err?.message || 'Shopify import failed.';
    setUploadError(message);
    onComplete && onComplete(false, message);
  } finally {
    setImporting(false);
  }
};
